// src/components/outputs/OutputPrimitives.jsx
//
// Shared building blocks of the one result renderer, PiecesResultEntry.jsx, and
// of its pieces/ widgets (ReadyToPublish, PieceRow):
// SectionLabel, TagChips, CopyBtn, MetaFooter and the smaller primitives below.
//
// They are the house style the LinkedIn renderer once set for generated output,
// extracted so that every format is literally the same component rather than
// an approximate copy (before this module each per-channel renderer carried its
// own `SectionLabel` and they drifted apart on padding, tint and spacing; those
// renderers were deleted in phase 5 of the output-structure proposal).
//
// Channel identity is carried by the `color` prop (the platform brand colour),
// never by the structure: the geometry, type scale and tints are constant across
// formats, only the accent changes.
//
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check } from 'lucide-react';
import { COLORS, FONT_MONO, RADII } from '../../constants/theme.js';

// The renderer receives a `w(opacity)` helper and a `t` theme object from the
// wizard so it adapts to the surface it is drawn on (wizard vs Run Detail).
// Defaults keep the primitives usable standalone.
const defaultW = (o) => `rgba(255,255,255,${o})`;

/** Field caption above a block — 10px mono, uppercase, wide tracking. */
export const SectionLabel = ({ children, color, t = {} }) => (
  <div
    style={{
      fontSize: '10px',
      fontFamily: FONT_MONO,
      color: color || t.textMuted || COLORS.textTertiary,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontWeight: 600,
      marginBottom: '6px',
    }}
  >
    {children}
  </div>
);

/**
 * A block of generated copy. `accent` draws the 3px left rule LinkedIn uses to
 * mark the hook; `emphasis` bumps size/weight for that same lead-in role.
 */
export const ContentBlock = ({ children, w = defaultW, accent, emphasis = false, style = {} }) => (
  <div
    style={{
      padding: emphasis ? '12px 16px' : '14px 16px',
      background: w(0.03),
      borderRadius: RADII.controlInner,
      fontSize: emphasis ? '15px' : '14px',
      fontWeight: emphasis ? 600 : 400,
      lineHeight: emphasis ? 1.5 : 1.7,
      whiteSpace: 'pre-wrap',
      ...(accent ? { borderLeft: `3px solid ${accent}` } : {}),
      ...style,
    }}
  >
    {children}
  </div>
);

/** Labelled block — the caption + body pairing that makes up most of a result. */
export const Field = ({ label, children, w = defaultW, t = {}, accent, emphasis, labelColor }) => {
  if (!children) return null;
  return (
    <div style={{ marginBottom: '12px' }}>
      <SectionLabel color={labelColor} t={t}>{label}</SectionLabel>
      <ContentBlock w={w} accent={accent} emphasis={emphasis}>{children}</ContentBlock>
    </div>
  );
};

/**
 * Hashtag / keyword chips, tinted with the channel colour.
 *
 * `separator` (default ' ') is printed between consecutive chips so a hand
 * selection of the row copies "#a #b" (or "a, b" for a comma-separated box),
 * never "#a#b". It sits in its own <span> with white-space: pre: a bare
 * whitespace text node between flex items is collapsed by CSS and left out
 * of the browser's selection text.
 */
export const TagChips = ({ tags, color, prefix = '#', separator = ' ', style = {} }) => {
  const chips = (tags || [])
    .map((tag) => String(tag || '').trim())
    .filter(Boolean)
    .map((s) => (prefix && !s.startsWith(prefix) ? `${prefix}${s}` : s));
  if (chips.length === 0) return null;
  // The chips space themselves (margin-right) instead of a column gap: a gap
  // would also pad both sides of the separator span.
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: '6px', marginBottom: '12px', ...style }}>
      {chips.map((text, i) => (
        <React.Fragment key={i}>
          {i > 0 && separator ? (
            <span style={{ whiteSpace: 'pre', fontSize: '12px', color: COLORS.textTertiary }}>{separator}</span>
          ) : null}
          <span
            style={{
              padding: '3px 10px',
              marginRight: '6px',
              borderRadius: RADII.pill,
              background: `${color}15`,
              color,
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {text}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Tinted aside — LinkedIn's "primo commento" box. Takes the channel colour so
 * the tint and border read as that platform without restating the geometry.
 */
export const Callout = ({ label, icon, color = COLORS.accent, action, children, footnote, style = {} }) => (
  <div
    style={{
      marginTop: '14px',
      padding: '14px 16px',
      borderRadius: RADII.control,
      background: `${color}10`,
      border: `1px solid ${color}26`,
      ...style,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
      <div
        style={{
          fontSize: '10px',
          fontFamily: FONT_MONO,
          color,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        {icon}
        {label}
      </div>
      {action}
    </div>
    <div style={{ fontSize: '13px', lineHeight: 1.5 }}>{children}</div>
    {footnote && (
      <div style={{ marginTop: '6px', fontSize: '10px', color: COLORS.textTertiary, fontFamily: FONT_MONO }}>
        {footnote}
      </div>
    )}
  </div>
);

/**
 * Inline copy control used inside Callout headers and section rows. `id` lands
 * on the <button> (a piece row passes its copy id so the DOM names it); it is
 * always type="button" so it never submits an enclosing form.
 */
export const CopyBtn = ({ active, onClick, label, t = {}, color, id }) => {
  const { t: tr } = useTranslation('outputs');
  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: RADII.controlInner,
        fontSize: '10.5px',
        fontWeight: 600,
        cursor: 'pointer',
        border: `1px solid ${color ? `${color}4D` : (t.border || COLORS.borderStrong)}`,
        background: 'transparent',
        color: color || t.textMuted || COLORS.textTertiary,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      {active ? <><Check size={11} /> {tr('common.copied')}</> : <><Copy size={11} /> {label}</>}
    </button>
  );
};

/** Footer stats row — word/char counts and similar, bullet-separated. */
export const MetaFooter = ({ items, style = {} }) => {
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <div
      style={{
        marginTop: '10px',
        fontSize: '11px',
        color: COLORS.textSecondary,
        fontFamily: FONT_MONO,
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        ...style,
      }}
    >
      {list.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden="true">•</span>}
          <span>{item}</span>
        </React.Fragment>
      ))}
    </div>
  );
};
